import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebNotification } from '../hooks/useWebNotification';
import { startPolling, onVisibilityFetch } from '../utils/pollingKeepAlive';
import { connectSocket, onNewMessage, offNewMessage } from '../utils/socket';
import socket from '../utils/socket';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  DotsThreeVertical, User, Users, WhatsappLogo, Phone, Globe, 
  MagnifyingGlass, X, Copy, Check, Link as LinkIcon, PhoneCall,
  ArrowLeft, Eye, Bell
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerSelect = ({ onChatSelect, isCompact = false, activeType = null }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { updateAppBadge, showNotification, playNotificationSound, permission, requestPermission } = useWebNotification();
  const [activeTab, setActiveTab] = useState('chats');
  const [showMenu, setShowMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [handlers, setHandlers] = useState(() => {
    // Try to restore cached handlers from sessionStorage for smooth transitions
    try {
      const cached = sessionStorage.getItem('dws_handlers_cache');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [unreadCounts, setUnreadCounts] = useState({});
  const [totalGroupUnread, setTotalGroupUnread] = useState(0);
  const [supportInfo, setSupportInfo] = useState({ phone: null, whatsapp: null, app_name: 'DWS Vault Chat' });
  const [displayNames, setDisplayNames] = useState({
    deposit_name: 'DWS Deposit',
    withdrawal_name: 'DWS Withdrawal',
    support_name: 'DWS Support',
    inquiry_name: 'DWS Team',
    deposit_dp: null,
    withdrawal_dp: null,
    support_dp: null,
    inquiry_dp: null,
  });
  const [showLinks, setShowLinks] = useState(false);
  const [customerLinks, setCustomerLinks] = useState([]);
  const [linksCopied, setLinksCopied] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [viewingStatus, setViewingStatus] = useState(null);
  const [statusProgress, setStatusProgress] = useState(0);
  const menuRef = useRef(null);
  const statusTimerRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const fetchUnreadRef = useRef(null);

  // Phase A — a device-recovery ("Trouble signing in?") session is restricted
  // to the Support area until the user recovers their password. Force them
  // back to their support chat if they try to reach the deposit/withdrawal hub.
  useEffect(() => {
    if (user?.support_restricted) {
      navigate('/customer-chat?type=support', { replace: true });
    }
  }, [user?.support_restricted, navigate]);


  // Connect socket for real-time notifications & calls
  useEffect(() => {
    if (!user?.id) return;
    connectSocket(user.id);

    // Skip notifications for first 2 seconds after mount (prevents PWA re-open notification)
    let allowNotifications = false;
    const startTimer = setTimeout(() => { allowNotifications = true; }, 2000);

    // Listen for incoming calls even on main page
    const handleIncomingCall = (data) => {
      if (data.caller_id !== user.id) {
        showNotification({ 
          title: 'Incoming Call', 
          body: `${data.caller_name || 'Someone'} is calling you`,
          tag: 'incoming-call'
        });
      }
    };

    // Listen for new messages - trigger notification + refresh unread
    const handleNewMessage = (data) => {
      // Only notify for messages from handlers/admins, NOT from customer themselves
      if (data.message && data.message.sender_id !== user.id && data.message.sender_role !== 'customer') {
        if (!isCompact && allowNotifications) {
          showNotification({ 
            title: 'New Message', 
            body: data.message.text || 'New message received',
            tag: `msg-${data.room_id}`,
            data: { roomId: data.room_id, chatType: data.chat_type || data.message?.chat_type || data.room_type }
          });
        }
        // Immediately refresh unread counts
        if (fetchUnreadRef.current) fetchUnreadRef.current();
      }
    };

    socket.on('incoming_call', handleIncomingCall);
    onNewMessage(handleNewMessage);

    return () => {
      clearTimeout(startTimer);
      socket.off('incoming_call', handleIncomingCall);
      offNewMessage(handleNewMessage);
    };
  }, [user]);

  // Fetch support info
  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/settings/support-info?t=${Date.now()}`);
        setSupportInfo(res.data);
      } catch (e) {}
    };
    fetch();
  }, []);

  // Fetch display names (admin-configured) so client sees "DWS Deposit / Withdrawal / Support"
  // instead of internal handler usernames like "MB1", "MB2".
  useEffect(() => {
    const fetchDisplay = async () => {
      try {
        const res = await axios.get(`${API}/settings/display-names?t=${Date.now()}`);
        if (res.data) setDisplayNames(prev => ({ ...prev, ...res.data }));
      } catch (e) {}
    };
    fetchDisplay();
  }, []);

  // Fetch handlers list
  useEffect(() => {
    const fetchHandlers = async () => {
      try {
        const res = await axios.get(`${API}/users/all-staff`);
        const staffList = res.data.filter(u => u.role !== 'super_admin');
        setHandlers(staffList);
        sessionStorage.setItem('dws_handlers_cache', JSON.stringify(staffList));
      } catch (e) {}
    };
    fetchHandlers();
  }, []);

  // Fetch unread counts
  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/chat/unread-count`, { params: { user_id: user.id } });
        const counts = { deposit: 0, withdrawal: 0, support: 0 };
        (res.data?.unread_rooms || []).forEach(r => {
          const t = r.chat_type === 'withdraw' ? 'withdrawal' : r.chat_type;
          if (t in counts) counts[t] += (r.count || 0);
        });
        setUnreadCounts(counts);
        updateAppBadge(Object.values(counts).reduce((s, c) => s + c, 0));
      } catch (e) {}
    };
    fetchUnreadRef.current = fetchUnread;
    fetchUnread();
    const stopPoll = startPolling('customer-unread', fetchUnread, 15000, { skipWhenHidden: true });
    const cleanupVis = onVisibilityFetch(fetchUnread);
    return () => { stopPoll(); cleanupVis(); };
  }, [user]);

  // Fetch group unread
  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/groups/user-groups/unread-count?user_id=${user.id}`);
        setTotalGroupUnread(res.data.total_unread || 0);
      } catch (e) {}
    };
    fetch();
    const stopPoll = startPolling('customer-group-unread', fetch, 15000, { skipWhenHidden: true });
    return () => stopPoll();
  }, [user]);

  // Fetch statuses for Updates tab
  useEffect(() => {
    if (activeTab !== 'updates') return;
    const fetchStatuses = async () => {
      try {
        const res = await axios.get(`${API}/status/all`);
        setStatuses(res.data || []);
      } catch (e) {}
    };
    fetchStatuses();
  }, [activeTab]);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Status auto-advance timer
  useEffect(() => {
    if (!viewingStatus) return;
    setStatusProgress(0);
    const startTime = Date.now();
    const duration = 10000; // 10 seconds
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * 100, 100);
      setStatusProgress(progress);
      if (progress >= 100) {
        // Auto-advance to next status
        const currentIdx = statuses.findIndex(s => s.id === viewingStatus.id);
        if (currentIdx < statuses.length - 1) {
          setViewingStatus(statuses[currentIdx + 1]);
        } else {
          setViewingStatus(null);
        }
        return;
      }
      statusTimerRef.current = requestAnimationFrame(tick);
    };
    statusTimerRef.current = requestAnimationFrame(tick);

    // Record view
    if (user?.id) {
      axios.post(`${API}/status/${viewingStatus.id}/view`, { viewer_id: user.id, viewer_name: user.username }).catch(() => {});
    }

    return () => { if (statusTimerRef.current) cancelAnimationFrame(statusTimerRef.current); };
  }, [viewingStatus]);

  const handleSelect = (type) => {
    if (onChatSelect) onChatSelect(type);
    else navigate(`/customer-chat?type=${type}`);
  };

  // Search messages across all chats
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2 || !user?.id) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await axios.get(`${API}/chat/search-messages`, { params: { user_id: user.id, query: searchQuery } });
        setSearchResults(res.data.results || []);
      } catch (e) { setSearchResults([]); }
      finally { setIsSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, user]);

  const getHandlerForType = (type) => {
    const roleMap = { deposit: 'deposit_handler', withdrawal: 'withdrawal_handler', support: 'support_handler' };
    return handlers.find(h => h.role === roleMap[type]);
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${API}${url}`;
  };

  const getTimeAgo = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const isPWA = typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone);
  const isSplitScreen = !!onChatSelect && !isPWA && window.innerWidth >= 1024;

  return (
    <div className={`${isSplitScreen ? 'h-full' : 'min-h-screen'} bg-white flex flex-col`} 
      style={!isSplitScreen ? { position: 'fixed', inset: 0 } : {}}>
      
      {/* Header - WhatsApp Style */}
      <div className="bg-[#008069] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/icons/icon-128x128.png" alt="DWS" className="w-8 h-8 rounded-full" />
            <div>
              <h1 className="text-lg font-bold">DWS Vault</h1>
              <p className="text-[10px] text-white/70">Chat</p>
            </div>
          </div>
          <div className="relative" ref={menuRef}>
            <button onClick={() => setShowMenu(!showMenu)} className="p-2 hover:bg-white/10 rounded-full" data-testid="customer-menu-btn">
              <DotsThreeVertical size={22} weight="bold" />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-xl border z-50 py-1">
                <button onClick={() => { setShowMenu(false); navigate('/customer-dashboard'); }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3">
                  <User size={20} className="text-gray-600" />
                  <div><p className="text-sm font-medium text-gray-800">Profile</p><p className="text-[10px] text-gray-500">View your profile</p></div>
                </button>
                <button onClick={() => { setShowMenu(false); navigate('/customer/groups'); }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3">
                  <Users size={20} className="text-gray-600" />
                  <div><p className="text-sm font-medium text-gray-800">Group Chat</p><p className="text-[10px] text-gray-500">Join group chats</p></div>
                </button>
                <button onClick={() => { setShowMenu(false); window.open(`https://wa.me/${supportInfo.whatsapp || ''}`, '_blank'); }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3">
                  <WhatsappLogo size={20} className="text-green-600" />
                  <div><p className="text-sm font-medium text-gray-800">WhatsApp Support</p><p className="text-[10px] text-gray-500">Chat on WhatsApp</p></div>
                </button>
                <button onClick={() => { setShowMenu(false); if (supportInfo.phone) window.location.href = `tel:${supportInfo.phone}`; else toast.info('Call support not configured'); }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3">
                  <Phone size={20} className="text-blue-600" />
                  <div><p className="text-sm font-medium text-gray-800">Call Support</p><p className="text-[10px] text-gray-500">{supportInfo.phone || 'Not configured'}</p></div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enable Notifications banner — gesture-triggered permission request so
          push actually works on mobile/PWA (browsers block auto-prompts). */}
      {permission !== 'granted' && (
        <button
          onClick={async () => {
            const p = await requestPermission();
            if (p === 'granted') toast.success('Notifications enabled! You will now get message alerts.');
            else if (p === 'denied') toast.error('Notifications blocked. Enable them in your browser/site settings.');
          }}
          data-testid="enable-notifications-banner"
          className="w-full flex items-center justify-center gap-2 bg-[#128C7E] text-white text-sm font-semibold px-4 py-2.5 flex-shrink-0 hover:bg-[#0e6e63] transition-colors"
        >
          <Bell size={18} weight="fill" />
          Enable notifications for new messages
        </button>
      )}

      {/* Tabs: Chats / Updates */}
      <div className="bg-[#008069] flex flex-shrink-0">
        <button onClick={() => setActiveTab('chats')}
          className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${activeTab === 'chats' ? 'text-white border-b-2 border-white' : 'text-white/60'}`}>
          Chats
        </button>
        <button onClick={() => setActiveTab('updates')}
          className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${activeTab === 'updates' ? 'text-white border-b-2 border-white' : 'text-white/60'}`}>
          Updates
        </button>
      </div>

      {activeTab === 'chats' ? (
        <>
          {/* Search */}
          <div className="px-3 py-2 bg-gray-50 flex-shrink-0">
            <div className="flex items-center bg-white rounded-lg px-3 py-2 border border-gray-200">
              <MagnifyingGlass size={18} className="text-gray-400 mr-2" />
              <input type="text" placeholder="Search chat..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400" data-testid="customer-search" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="p-0.5"><X size={16} className="text-gray-400" /></button>}
            </div>
          </div>

          {/* Search Results */}
          {searchQuery.length >= 2 ? (
            <div className="flex-1 overflow-y-auto">
              {isSearching ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-[#008069] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : searchResults.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-8">No messages found for "{searchQuery}"</p>
              ) : (
                searchResults.map((result, idx) => (
                  <div key={idx} onClick={() => { setSearchQuery(''); handleSelect(result.chat_type); }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-[#008069] flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{(result.sender_name || 'U')[0].toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-800 text-sm">{result.sender_name}</span>
                        <span className="text-[10px] text-gray-500 capitalize">{result.chat_type}</span>
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{result.text}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
          /* Chat List */
          <div className="flex-1 overflow-y-auto">
            {/* Deposit Handler */}
            {(() => {
              const h = getHandlerForType('deposit');
              const name = displayNames.deposit_name || 'DWS Deposit';
              const dp = displayNames.deposit_dp || h?.profile_pic;
              if (searchQuery && !name.toLowerCase().includes(searchQuery.toLowerCase())) return null;
              return (
                <div onClick={() => handleSelect('deposit')}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 ${activeType === 'deposit' ? 'bg-[#f0faf7]' : ''}`}
                  data-testid="deposit-chat-item">
                  <div className="w-12 h-12 rounded-full bg-[#00a884] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {dp ? <img src={getImageUrl(dp)} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-white font-bold text-lg">{name[0].toUpperCase()}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-800 text-sm">{name}</span>
                      <span className="text-[10px] text-gray-500">Deposit</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">Tap to chat</p>
                  </div>
                  {(unreadCounts.deposit || 0) > 0 && (
                    <div className="bg-[#00a884] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{unreadCounts.deposit}</div>
                  )}
                </div>
              );
            })()}

            {/* Withdrawal Handler */}
            {(() => {
              const h = getHandlerForType('withdrawal');
              const name = displayNames.withdrawal_name || 'DWS Withdrawal';
              const dp = displayNames.withdrawal_dp || h?.profile_pic;
              if (searchQuery && !name.toLowerCase().includes(searchQuery.toLowerCase())) return null;
              return (
                <div onClick={() => handleSelect('withdrawal')}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 ${activeType === 'withdrawal' ? 'bg-[#f0faf7]' : ''}`}
                  data-testid="withdrawal-chat-item">
                  <div className="w-12 h-12 rounded-full bg-[#5856d6] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {dp ? <img src={getImageUrl(dp)} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-white font-bold text-lg">{name[0].toUpperCase()}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-800 text-sm">{name}</span>
                      <span className="text-[10px] text-gray-500">Withdrawal</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">Tap to chat</p>
                  </div>
                  {(unreadCounts.withdrawal || 0) > 0 && (
                    <div className="bg-[#00a884] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{unreadCounts.withdrawal}</div>
                  )}
                </div>
              );
            })()}

            {/* Support Handler */}
            {(() => {
              const h = getHandlerForType('support');
              const name = displayNames.support_name || 'DWS Support';
              const dp = displayNames.support_dp || h?.profile_pic;
              if (searchQuery && !name.toLowerCase().includes(searchQuery.toLowerCase())) return null;
              return (
                <div onClick={() => handleSelect('support')}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 ${activeType === 'support' ? 'bg-[#f0faf7]' : ''}`}
                  data-testid="support-chat-item">
                  <div className="w-12 h-12 rounded-full bg-[#ff9500] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {dp ? <img src={getImageUrl(dp)} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-white font-bold text-lg">{name[0].toUpperCase()}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-800 text-sm">{name}</span>
                      <span className="text-[10px] text-gray-500">Support</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">Tap to chat</p>
                  </div>
                  {(unreadCounts.support || 0) > 0 && (
                    <div className="bg-[#00a884] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{unreadCounts.support}</div>
                  )}
                </div>
              );
            })()}

            {/* Groups */}
            <div onClick={() => navigate('/customer/groups')}
              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-testid="groups-item">
              <div className="w-12 h-12 rounded-full bg-[#4ade80] flex items-center justify-center flex-shrink-0">
                <Users size={24} className="text-white" weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-gray-800 text-sm">Groups</span>
                <p className="text-xs text-gray-500 mt-0.5">Your group chats</p>
              </div>
              {totalGroupUnread > 0 && (
                <div className="bg-[#00a884] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{totalGroupUnread}</div>
              )}
            </div>

            {/* Links */}
            <div onClick={async () => {
              try { const res = await axios.get(`${API}/links`); setCustomerLinks(res.data || []); setShowLinks(true); } catch (e) {}
            }} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-testid="links-item">
              <div className="w-12 h-12 rounded-full bg-[#3b82f6] flex items-center justify-center flex-shrink-0">
                <LinkIcon size={24} className="text-white" weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-gray-800 text-sm">Links</span>
                <p className="text-xs text-gray-500 mt-0.5">Quick links</p>
              </div>
            </div>

            {/* Call Support */}
            {supportInfo.phone && (
              <div onClick={() => window.location.href = `tel:${supportInfo.phone}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-testid="call-support-item">
                <div className="w-12 h-12 rounded-full bg-[#06b6d4] flex items-center justify-center flex-shrink-0">
                  <PhoneCall size={24} className="text-white" weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-gray-800 text-sm">Call Support</span>
                  <p className="text-xs text-gray-500 mt-0.5">{supportInfo.phone}</p>
                </div>
              </div>
            )}
          </div>
          )}
        </>
      ) : (
        /* Updates Tab */
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-4">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Status</h3>
            {statuses.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-8">No status updates</p>
            ) : (
              <div className="space-y-1 bg-white rounded-xl overflow-hidden shadow-sm">
                {statuses.map((status, idx) => (
                  <div key={status.id || idx} onClick={() => setViewingStatus(status)}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0">
                    <div className="w-12 h-12 rounded-full ring-2 ring-[#008069] ring-offset-2 overflow-hidden bg-[#008069] flex items-center justify-center flex-shrink-0">
                      {status.type === 'image' && status.image_url ?
                        <img src={getImageUrl(status.image_url)} alt="" className="w-full h-full object-cover" /> :
                        <span className="text-white font-bold">{(status.user_name || 'D')[0].toUpperCase()}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-800 text-sm">DWS Team</span>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {status.type === 'text' ? status.content : (status.caption || (status.type === 'audio' ? 'Audio' : 'Photo'))} · {getTimeAgo(status.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status Fullscreen Viewer */}
      {viewingStatus && (
        <div className="fixed inset-0 bg-black z-[100] flex flex-col" data-testid="customer-status-viewer">
          {/* Progress Bar */}
          <div className="h-1 bg-white/20 flex-shrink-0">
            <div className="h-full bg-white transition-all duration-100" style={{ width: `${statusProgress}%` }} />
          </div>
          {/* Header */}
          <div className="flex items-center gap-3 p-3 bg-black/50 flex-shrink-0">
            <button onClick={() => setViewingStatus(null)} className="text-white p-1"><ArrowLeft size={24} weight="bold" /></button>
            <div className="w-8 h-8 rounded-full bg-[#008069] flex items-center justify-center">
              <span className="text-white font-bold text-sm">D</span>
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">DWS Team</p>
              <p className="text-white/60 text-xs">{getTimeAgo(viewingStatus.created_at)}</p>
            </div>
          </div>
          {/* Content — `min-h-0` prevents portrait status images from
              overflowing the fixed viewport (default `min-height: auto` on
              flex-1 was allowing the image to push past the bottom edge). */}
          <div className="flex-1 min-h-0 flex items-center justify-center p-4">
            {viewingStatus.type === 'image' && viewingStatus.image_url ?
              <img src={getImageUrl(viewingStatus.image_url)} alt="" className="max-w-full max-h-full object-contain" /> :
              viewingStatus.type === 'audio' && viewingStatus.audio_url ?
                <div className="bg-white/10 rounded-2xl p-8 flex flex-col items-center gap-4">
                  <audio src={getImageUrl(viewingStatus.audio_url)} controls autoPlay className="w-72" />
                </div> :
                <div className="bg-[#008069] rounded-2xl p-8 max-w-md">
                  <p className="text-white text-xl text-center font-medium">{viewingStatus.content}</p>
                </div>}
          </div>
          {viewingStatus.caption && (
            <div className="p-4 bg-black/60 flex-shrink-0"><p className="text-white text-center text-sm whitespace-pre-line">{viewingStatus.caption}</p></div>
          )}
        </div>
      )}

      {/* Links Modal */}
      {showLinks && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50" onClick={() => setShowLinks(false)}>
          <div className="bg-white w-full sm:w-[420px] sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="bg-[#008069] text-white px-5 py-4 sm:rounded-t-2xl rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3"><Globe size={24} /><h2 className="text-lg font-bold">Links</h2></div>
              <button onClick={() => setShowLinks(false)} className="p-1.5 hover:bg-white/20 rounded-full"><X size={22} weight="bold" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {customerLinks.length === 0 ? (
                <p className="text-center py-10 text-gray-400 text-sm">No links available</p>
              ) : customerLinks.map(link => (
                <div key={link.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 text-sm mb-1">{link.description}</h3>
                      <a href={link.url} onClick={(e) => { e.preventDefault(); window.open(link.url, '_blank', 'noopener,noreferrer'); }} className="text-[#008069] text-xs break-all hover:underline">{link.url}</a>
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(link.url); setLinksCopied(link.id); setTimeout(() => setLinksCopied(null), 2000); }}
                      className="p-2 hover:bg-gray-200 rounded-full flex-shrink-0">
                      {linksCopied === link.id ? <Check size={18} className="text-[#008069]" /> : <Copy size={18} className="text-gray-500" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSelect;
